// book-error-class.js -- coded class for a book script's api_error / script_error.
// Single job: turn the script's free-text error into one closed code, plus the HTTP status or the
// JSON-RPC error code when the script reported one (#1711), so a failed sync says what the book answered. Pure; the free text
// never leaves the device (PostHog gets the code only -- analytics-scrub closes the enum again).
// Origin: 2026-10-14, four BetMGM api_error exits in 1-3 s with no way to see the answer
// (device Sentry receives nothing; the attempt ledger records only reason_code).
import { BOOK_ERROR_CLASSES } from './attempt-exit-map';

export { BOOK_ERROR_CLASSES };

// WebKit / Chromium fetch rejection messages (TypeError text), matched loosely.
const FETCH_FAILED_RE = /load failed|failed to fetch|network|cancell?ed|hostname|timed out|offline/i;
const HTTP_RE = /^HTTP (\d{3})$/;
// #1711: DraftKings' JSON-RPC error, optionally with its integer code (int32, canonical form only:
// no leading zeros, no -0).
const RPC_RE = /^rpc_error(?: (0|-?[1-9]\d{0,9}))?$/;
const INT32_MIN = -2147483648;
const INT32_MAX = 2147483647;

const EXACT = Object.freeze({
  'betslips not array': 'no_bets_list',
  'fetch_error on first page': 'first_page_fetch_error',
  'parse_error on first page': 'first_page_parse_error',
  settled_fetch_error: 'settled_fetch_error',
  not_available_in_state: 'not_available_in_state',
  socket_closed: 'socket_closed',
  frame_parse_failed: 'frame_parse_failed',
  // #1711: DraftKings (books/dk.js)
  socket_ctor: 'socket_ctor',
  frame_timeout: 'frame_timeout',
  bets_not_array: 'no_bets_list',
});

// parsed: parseBetslipsMessage output. Returns { errorClass, httpStatus } for api_error /
// script_error, else null. An rpc_error also carries rpcCode (an int32, or null when DK sent none).
export function classifyBookError(parsed) {
  if (!parsed || (parsed.type !== 'api_error' && parsed.type !== 'script_error')) return null;
  const err = typeof parsed.error === 'string' ? parsed.error : '';
  const none = (errorClass) => ({ errorClass, httpStatus: null });
  if (err === '') return none('no_detail');
  const m = HTTP_RE.exec(err);
  if (m) {
    const s = Number(m[1]);
    return s >= 100 && s <= 599 ? { errorClass: 'http_status', httpStatus: s } : none('other');
  }
  const r = RPC_RE.exec(err);
  if (r) {
    if (r[1] === undefined) return { errorClass: 'rpc_error', httpStatus: null, rpcCode: null };
    const code = Number(r[1]);
    return code >= INT32_MIN && code <= INT32_MAX ? { errorClass: 'rpc_error', httpStatus: null, rpcCode: code } : none('other');
  }
  if (Object.prototype.hasOwnProperty.call(EXACT, err)) return none(EXACT[err]);
  if (err.startsWith('script_exception')) return none('script_exception');
  if (FETCH_FAILED_RE.test(err)) return none('fetch_failed');
  return none('other');
}
